import {map} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {settingsApi} from 'api';
import {
    ActionButton, Card, Button, ConfirmModal, CopyText, Table, TableBody, TableCell, TableRow,
    DataWrapper, PredefinedNotifierType
} from 'components';
import {CLIENT_DEVICE_TRANSLATE} from 'constants/session';
import {useApiData} from 'hooks';
import {ClientDevice} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CLIENT_SETTINGS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

export const CreateSessionTokens: React.FC = () => {
    const {t} = useTranslation();
    const [confirmModalProps, setConfirmModalProps] = useState<{
        open: boolean,
        device?: ClientDevice
    }>({open: false, device: null});

    const {
        send: getStartSessionTokens, data: tokenObject, loading
    } = useApiData(settingsApi.getStartSessionToken);
    const {send: updateStartSessionToken} = useApiData(settingsApi.updateStartSessionTokens, {
        onSuccess: getStartSessionTokens,
        notificationType: PredefinedNotifierType.Save
    });

    const handleConfirmModalOpen = useCallback((device: ClientDevice) => {
        setConfirmModalProps({device, open: true});
    }, []);

    const handleConfirmModalClose = useCallback(async (result: boolean) => {
        if (result) {
            await updateStartSessionToken(confirmModalProps.device);
        }
        setConfirmModalProps({open: false});
    }, [confirmModalProps, updateStartSessionToken]);

    const headers = useMemo(() => [
        {children: t(T_KEYS.CREATE_SESSION_TOKENS.TYPE), className: 'tw-w-48'},
        {children: t(T_KEYS.CREATE_SESSION_TOKENS.TOKEN)},
        {children: '', className: 'tw-w-48'},
    ], [t]);

    const configurableDevices = useMemo(() => [ClientDevice.Browser, ClientDevice.AndroidSDK, ClientDevice.IosSDK], []);
    const getClientDeviceName = useCallback((device: ClientDevice) => t(CLIENT_DEVICE_TRANSLATE[device]), [t]);

    const renderGenerateTokenButton = useCallback((device: ClientDevice) => (
        <ActionButton
            action='create'
            hideIcon
            onClick={() => handleConfirmModalOpen(device)}
        >
            {t(T_KEYS.CREATE_SESSION_TOKENS.GENERATE)}
        </ActionButton>
    ), [t, handleConfirmModalOpen]);

    useEffect(() => {
        getStartSessionTokens();
    }, [getStartSessionTokens]);

    return (
        <Fragment>
            <div className='tw-mb-4'>{t(T_KEYS.DESCRIPTION)}</div>
            <Table loading={loading} headers={headers} className='-md:tw-hidden'>
                <TableBody>
                    {map(configurableDevices, (device) => (
                        <TableRow key={device}>
                            <TableCell>{getClientDeviceName(device)}</TableCell>
                            <TableCell>
                                <CopyText text={tokenObject?.[device]} copiedLabelOnly labelPlacement='left' />
                            </TableCell>
                            <TableCell>
                                {renderGenerateTokenButton(device)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className='md:tw-hidden'>
                <DataWrapper isLoading={loading}>
                    {map(ClientDevice, (device) => (
                        <Card key={device} className='tw-mb-6'>
                            <div className='tw-font-bold tw-text-lg'>{getClientDeviceName(device)}</div>
                            <div className='tw-mt-2'>{tokenObject?.[device]}</div>
                            <div className='tw-grid sm:tw-grid-cols-2 tw-gap-2 tw-mt-4'>
                                <CopyText className='tw-flex-col tw-items-stretch' text={tokenObject?.[device]} copiedLabelOnly>
                                    <Button width='full'>
                                        {t(T_KEYS_COMMON.BUTTONS.COPY)}
                                    </Button>
                                </CopyText>
                                {renderGenerateTokenButton(device)}
                            </div>
                        </Card>
                    ))}
                </DataWrapper>
            </div>
            <ConfirmModal
                open={confirmModalProps.open}
                title={getClientDeviceName(confirmModalProps.device)}
                content={t(T_KEYS.CREATE_SESSION_TOKENS.CONFIRM)}
                onClose={handleConfirmModalClose}
            />
        </Fragment>
    );
};